import React from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';

export interface LayeredTutorBookingAvatarsProps {
  /** Larger back avatar — usually the tutor. */
  primaryImageUrl?: string | null;
  primaryName: string;
  primaryTooltip?: string;
  /** Smaller front avatar — usually the student. */
  secondaryImageUrl?: string | null;
  secondaryName: string;
  secondaryTooltip?: string;
  size?: number;
}

/**
 * Overlapping rounded profile pair for host tutor-booking cards
 * (tutor behind, student in front) so both parties are visible at a glance.
 */
const LayeredTutorBookingAvatars: React.FC<LayeredTutorBookingAvatarsProps> = ({
  primaryImageUrl,
  primaryName,
  primaryTooltip,
  secondaryImageUrl,
  secondaryName,
  secondaryTooltip,
  size = 48,
}) => {
  const secondarySize = Math.round(size * 0.62);
  const overlap = Math.round(size * 0.28);

  const primaryAvatar = (
    <Avatar
      src={primaryImageUrl?.trim() || undefined}
      alt=""
      variant="rounded"
      sx={{
        width: size,
        height: size,
        bgcolor: '#4AA7C6',
        color: '#ffffff',
        fontSize: size >= 48 ? '1.125rem' : '0.875rem',
        fontWeight: 600,
        border: '2px solid',
        borderColor: 'background.paper',
        boxSizing: 'border-box',
      }}
    >
      {tutorAvatarInitials(primaryName)}
    </Avatar>
  );

  const secondaryAvatar = (
    <Avatar
      src={secondaryImageUrl?.trim() || undefined}
      alt=""
      variant="rounded"
      sx={{
        position: 'absolute',
        right: -overlap * 0.35,
        bottom: -overlap * 0.35,
        width: secondarySize,
        height: secondarySize,
        bgcolor: '#6B8F71',
        color: '#ffffff',
        fontSize: '0.7rem',
        fontWeight: 700,
        border: '2px solid',
        borderColor: 'background.paper',
        boxSizing: 'border-box',
        boxShadow: (theme) => `0 1px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.12)'}`,
      }}
    >
      {tutorAvatarInitials(secondaryName)}
    </Avatar>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: size + overlap * 0.45,
        height: size + overlap * 0.45,
        flexShrink: 0,
      }}
    >
      {primaryTooltip ? (
        <Tooltip title={primaryTooltip} enterDelay={400}>
          <Box sx={{ display: 'inline-flex' }}>{primaryAvatar}</Box>
        </Tooltip>
      ) : (
        primaryAvatar
      )}
      {secondaryTooltip ? (
        <Tooltip title={secondaryTooltip} enterDelay={400}>
          <Box sx={{ display: 'inline-flex' }}>{secondaryAvatar}</Box>
        </Tooltip>
      ) : (
        secondaryAvatar
      )}
    </Box>
  );
};

export default LayeredTutorBookingAvatars;
